import React, {
 useState,
 useCallback,
 useEffect,
} from 'react'
import {Option, TicketFormState} from 'models/Ticket'
import {
 createTicketApi,
 getCommonDropdownApi,
 getIssueTypesApi,
 getModulesApi,
} from 'utils/api/auth/ticketService'
import UploadIcon from '../../../img/uploadIcon.svg'
import CustomDropdown from './CustomDropdown'
import {toast} from 'react-toastify'
import {uploadFile} from 'utils/api/auth/mediaService'
import {useTranslation} from 'react-i18next'

type Props = {
 open: boolean
 onClose: () => void
}

type PriorityOption = {
 id: string
 label: string
}

const INITIAL_STATE: TicketFormState = {
 module_id: null,
 issue_type_id: null,
 description: '',
 priority: null,
 attachments: [],
}
type PreviewAttachment = {
 id: string
 type: 'image' | 'video'
 url: string
 original_filepath: string
}
const TicketModal: React.FC<Props> = ({open, onClose}) => {
 const {t} = useTranslation()
 const [formState, setFormState] =
  useState<TicketFormState>(INITIAL_STATE)
 const [loading, setLoading] = useState(false)
 const [modules, setModules] = useState<Option[]>([])
 const [issueTypes, setIssueTypes] = useState<Option[]>([])
 const [priorities, setPriorities] = useState<
  PriorityOption[]
 >([])

 const [preview, setPreview] =
  useState<PreviewAttachment | null>(null)

 const updateField = useCallback(
  <K extends keyof TicketFormState>(
   key: K,
   value: TicketFormState[K]
  ) => {
   setFormState((prev) => ({...prev, [key]: value}))
  },
  []
 )

 const isOtherSelected = useCallback(() => {
  const selectedModule = modules.find(
   (m) => m.id === formState.module_id
  )
  const selectedIssue = issueTypes.find(
   (i) => i.id === formState.issue_type_id
  )

  return (
   selectedModule?.label === 'Other' ||
   selectedIssue?.label === 'Other'
  )
 }, [
  modules,
  issueTypes,
  formState.module_id,
  formState.issue_type_id,
 ])

 const handleFileUpload = async (
  e: React.ChangeEvent<HTMLInputElement>
 ) => {
  const files = Array.from(e.target.files || [])
  if (!files.length) return

  for (const file of files) {
   const formData = new FormData()
   const fileType = file.type.startsWith('video')
    ? 'video'
    : 'image'

   formData.append('file', file)
   formData.append('file_type', fileType)
   formData.append('document_type', 'ticket_attachment')

   try {
    const res = await uploadFile(formData)
    const uploadedUrl = res.presigned_url || ''

    setFormState((prev) => ({
     ...prev,
     attachments: [
      ...prev.attachments,
      {
       id: crypto.randomUUID(),
       type: fileType,
       url: uploadedUrl,
       original_filepath: res.original_filepath || '',
      },
     ],
    }))
   } catch {
    toast.error('File upload failed')
   }
  }

  e.target.value = ''
 }

 const removeAttachment = (id: string) => {
  setFormState((prev) => ({
   ...prev,
   attachments: prev.attachments.filter(
    (att) => att.id !== id
   ),
  }))
 }

 const isFormValid =
  !!formState.module_id &&
  !!formState.issue_type_id &&
  !!formState.priority &&
  (!isOtherSelected() ||
   formState.description.trim().length > 0)

 const handleSubmit = async () => {
  if (!isFormValid) return
  setLoading(true)

  const payload = {
   module_id: formState.module_id as number,
   issue_type_id: formState.issue_type_id as number,
   priority: formState.priority as string,
   description: formState.description,
   attachments: formState.attachments.map((a) => ({
    type: a.type,
    url: a.original_filepath,
   })),
  }

  const result = await createTicketApi(payload)

  setLoading(false)

  if (result.success) {
   formState.attachments.forEach((a) =>
    URL.revokeObjectURL(a.url)
   )
   setFormState(INITIAL_STATE)
   toast.success(result.data.message)
   onClose()
  }
 }

 useEffect(() => {
  if (!isOtherSelected() && formState.description) {
   updateField('description', '')
  }
 }, [isOtherSelected])

 useEffect(() => {
  if (!open || modules.length) return

  const fetchModules = async () => {
   const res = await getModulesApi()
   setModules(res.map((m) => ({id: m.id, label: m.name})))
  }

  void fetchModules()
 }, [open])

 useEffect(() => {
  if (!open || priorities.length) return

  const fetchPriorities = async () => {
   const res = await getCommonDropdownApi('ticket_priority')

   setPriorities(
    res.map((p) => ({
     id: p.value,
     label: p.label,
    }))
   )
  }

  void fetchPriorities()
 }, [open])

 useEffect(() => {
  if (!formState.module_id) {
   setIssueTypes([])
   return
  }

  const fetchIssues = async () => {
   const moduleId = formState.module_id
   if (moduleId === null) return
   const res = await getIssueTypesApi(moduleId)
   setIssueTypes(
    res.map((i) => ({id: i.id, label: i.name}))
   )
  }

  void fetchIssues()
 }, [formState.module_id])
 if (!open) return null

 return (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
   <div className='w-[720px] rounded-2xl bg-white p-8 shadow-xl'>
    <div className='mb-6 flex items-center justify-between'>
     <h2 className='text-xl font-semibold text-gray-900'>
      {t('ticket.ticketModal.raiseTicket')}
     </h2>
     <button
      onClick={onClose}
      className='h-9 w-9 rounded-full bg-[#E6EBF0] text-gray-700 hover:bg-gray-200 font-extrabold'
     >
      ✕
     </button>
    </div>
    <div className='grid grid-cols-2 gap-5'>
     <CustomDropdown
      label={t('ticket.ticketModal.label1')}
      placeholder={t('ticket.ticketModal.selectModule')}
      value={formState.module_id}
      options={modules}
      onChange={(id) => {
       updateField('module_id', id)
       updateField('issue_type_id', null)
       updateField('description', '')
      }}
     />

     <CustomDropdown
      label={t('ticket.ticketModal.label2')}
      placeholder={t('ticket.ticketModal.selectIssueType')}
      value={formState.issue_type_id}
      options={issueTypes}
      disabled={!formState.module_id}
      onChange={(id) => {
       updateField('issue_type_id', id)
       updateField('description', '')
      }}
     />
     <CustomDropdown
      label={t('ticket.ticketModal.label3')}
      placeholder={t(
       'ticket.ticketModal.selectIssuePriority'
      )}
      value={formState.priority}
      options={priorities}
      onChange={(id) => {
       updateField('priority', id as string)
      }}
     />
    </div>

    <div
     className={`mt-5 rounded-lg border p-4 border-gray-200
    ${
     isOtherSelected() && formState.description
      ? 'bg-[#F5F8FA]'
      : ''
    }
    `}
    >
     <p className='mb-1 text-xs font-medium text-[#4E83E4]'>
      {t('ticket.ticketModal.issueDescription')}
      {isOtherSelected() && (
       <span className='text-red'>*</span>
      )}
     </p>

     <textarea
      rows={1}
      className='w-full resize-none bg-transparent text-sm outline-none overflow-hidden'
      placeholder={t('ticket.ticketModal.enter')}
      value={formState.description}
      onChange={(e) => {
       updateField('description', e.target.value)
       e.target.style.height = 'auto'
       e.target.style.height = `${e.target.scrollHeight}px`
      }}
     />
    </div>

    <div className='mt-6'>
     <p className='text-sm font-medium text-gray-700'>
      {t('ticket.ticketModal.imageLabel')}
     </p>
     <p className='font-normal mb-3 text-sm text-[#80868C]'>
      {t('ticket.ticketModal.supportText')}
     </p>

     <div className='flex flex-wrap gap-4'>
      {formState.attachments.map((att) => (
       <div
        key={att.id}
        className='relative w-24 cursor-pointer'
        onClick={() => setPreview(att)}
       >
        <div
         onClick={(e) => {
          e.stopPropagation()
          removeAttachment(att.id)
         }}
         className='absolute -right-0 bg-red-500 px-1 text-xs text-white'
         style={{
          borderWidth: 1,
          borderColor: '#E6EBF0',
          borderBottomLeftRadius: 8,
          borderTopRightRadius: 8,
         }}
        >
         <svg
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
         >
          <path
           d='M3 6H21'
           stroke='white'
           strokeWidth='2'
           strokeLinecap='round'
          />
          <path
           d='M8 6V4H16V6'
           stroke='white'
           strokeWidth='2'
           strokeLinecap='round'
          />
          <rect
           x='6'
           y='6'
           width='12'
           height='14'
           rx='2'
           stroke='white'
           strokeWidth='2'
          />
          <path
           d='M10 11V17M14 11V17'
           stroke='white'
           strokeWidth='2'
           strokeLinecap='round'
          />
         </svg>
        </div>

        {att.type === 'image' ? (
         <img
          src={att.url}
          className='h-20 w-24 rounded-lg object-cover'
         />
        ) : (
         <video
          src={att.url}
          className='h-20 w-24 rounded-lg object-cover pointer-events-none'
         />
        )}
       </div>
      ))}

      <label className='flex h-20 w-24 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-blue-400 bg-blue-50'>
       <input
        type='file'
        multiple
        accept='image/*,video/*'
        hidden
        onChange={handleFileUpload}
       />
       <img
        src={UploadIcon}
        alt='close'
        className='h-[34px] w-[34px]'
       />
      </label>
     </div>
    </div>
    <button
     onClick={handleSubmit}
     className={`mt-8 w-full rounded-lg py-3 text-base font-medium
            ${
             !isFormValid || loading
              ? 'bg-[#F2F2F2] text-gray-400'
              : 'bg-[#4E83E4] text-white hover:bg-blue-700'
            }`}
    >
     {loading
      ? t('ticket.ticketModal.submitting')
      : t('ticket.ticketModal.submit')}
    </button>
   </div>
   {preview && (
    <div className='fixed inset-0 z-[999] flex items-center justify-center bg-black'>
     <button
      className='absolute top-6 right-6 text-white text-3xl'
      onClick={() => setPreview(null)}
     >
      ✕
     </button>

     {preview.type === 'image' ? (
      <img
       src={preview.url}
       className='max-h-[90vh] max-w-[90vw] rounded-lg object-contain'
      />
     ) : (
      <video
       src={preview.url}
       controls
       autoPlay
       className='max-h-[90vh] max-w-[90vw] rounded-lg bg-black'
      />
     )}
    </div>
   )}
  </div>
 )
}

export default TicketModal
